<template>
  <!-- Pages sit on the space label's text edge; the docked panel and the rail's flyout share this tree. -->
  <div class="ml-6 flex flex-col pb-1.5 pr-0.5 pt-0.5">
    <template
      v-for="group in groupMenuItems(section.items)"
      :key="`${section.key}-${group.name}-${group.items[0]?.path}`"
    >
      <p
        v-if="group.name"
        :style="{
          '--i': staggerIndex(group.items[0]),
          '--row': rowOrder?.get(group.items[0]?.path)
        }"
        class="focus-page focus-row px-2.5 pb-1 pt-2.5 text-xs font-medium text-surface-500"
      >
        {{ group.name }}
      </p>
      <TwnFocusSidebarLink
        v-for="item in group.items"
        :key="item.path"
        :is-current="isMenuItemActive(item, currentPath)"
        :path="item.path"
        :style="{ '--i': staggerIndex(item), '--row': rowOrder?.get(item.path) }"
        class="focus-page focus-row relative"
        @navigate="emit('navigate')"
      >
        <!-- A tree under the space's icon, in the highlight's colour: each page carries its stretch, the current one branches off. -->
        <span
          v-if="!isMenuItemActive(item, currentPath)"
          aria-hidden="true"
          class="pointer-events-none absolute left-[calc(-0.5rem-0.75px)] w-[1.5px] bg-selected inset-y-0"
        />
        <template v-else>
          <!-- The line gives way to the elbow here, so the two never overlap and darken. -->
          <span
            aria-hidden="true"
            class="pointer-events-none absolute left-[calc(-0.5rem-0.75px)] w-[1.5px] bg-selected top-0 h-[calc(50%-8px)]"
          />
          <span
            aria-hidden="true"
            class="pointer-events-none absolute left-[calc(-0.5rem-0.75px)] w-[1.5px] bg-selected bottom-0 top-1/2"
          />
          <!-- public/icons/line-expand.svg, inline so its stroke takes the highlight's colour. -->
          <svg
            aria-hidden="true"
            class="pointer-events-none absolute left-[calc(-0.5rem-1px)] top-[calc(50%-9px)] h-[10px] w-[9px] text-selected"
            fill="none"
            viewBox="0 0 9 10"
          >
            <path
              d="M1 1V4.68594C1 6.79223 2.26977 8.5 3.83587 8.5H7.5"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-miterlimit="10"
              stroke-width="1.5"
            />
          </svg>
        </template>
        <span class="line-clamp-2">{{ item.name }}</span>
        <span
          v-if="item.badge"
          class="ml-auto rounded-full bg-primary-700 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-surface-0"
        >
          {{ item.badge }}
        </span>
      </TwnFocusSidebarLink>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type {
  TwnFocusSidebarPagesEmits,
  TwnFocusSidebarPagesProps
} from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-pages/TwnFocusSidebarPages.types'
import type { MenuSection } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'

import TwnFocusSidebarLink from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-link/TwnFocusSidebarLink.vue'
import { FOCUS_PAGES_STAGGER_STEPS } from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-pages/TwnFocusSidebarPages.consts'
import { groupMenuItems, isMenuItemActive } from '@/domains/admin-panel/utils/focus-sidebar/focusSidebar.utils'

const props = defineProps<TwnFocusSidebarPagesProps>()
const emit = defineEmits<TwnFocusSidebarPagesEmits>()

const staggerIndex = (item: MenuSection['items'][number] | undefined): number =>
  Math.min(item ? props.section.items.indexOf(item) : 0, FOCUS_PAGES_STAGGER_STEPS)
</script>
